/**
 * MEMELOLO — Influencer attribution engine (see docs/influencers/README.md)
 *
 * - On a landing page (one that renders the `influencer-config` section) it stores
 *   the influencer handle + discount code in session cookies and rewrites every
 *   product link so it carries the discount and the `ref`.
 * - On any page of a referred visitor it writes the influencer into the cart
 *   attributes (they travel to abandoned checkouts and orders) and keeps the
 *   discount code applied to the cart.
 * - Exposes `window.mmTrack(name, data)` for analytics events.
 */
import { ThemeEvents, DiscountUpdateEvent } from '@theme/events';

const COOKIE_REF = 'mm_ref';
const COOKIE_DISCOUNT = 'mm_disc';
const COOKIE_LANDING = 'mm_land';
const COOKIE_NAME = 'mm_name';
const COOKIE_LINKS = 'mm_links';

/**
 * Focus mode lives in sessionStorage: it lasts for the visit that started on the landing
 * (this tab) and never carries over to a later visit typed as memelolo.com. Browsers keep
 * session cookies across restarts, which made the landing mode stick; storage does not.
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
  try {
    return sessionStorage.getItem(name) || '';
  } catch (_) {
    return '';
  }
}

/**
 * @param {string} name
 * @param {string} value
 */
function setCookie(name, value) {
  try {
    sessionStorage.setItem(name, value);
  } catch (_) {}
}

/**
 * @returns {{ handle: string, display_name: string, discount_code: string, utm_campaign: string } | null}
 */
function readLandingConfig() {
  const element = document.getElementById('mm-influencer');
  if (!element) return null;
  try {
    const config = JSON.parse(element.textContent || '{}');
    const handle = String(config.handle || '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, '');
    if (!handle) return null;
    return {
      handle,
      display_name: String(config.display_name || handle).trim(),
      discount_code: String(config.discount_code || '').trim(),
      utm_campaign: String(config.utm_campaign || handle),
    };
  } catch (_) {
    return null;
  }
}

const landing = readLandingConfig();

if (landing) {
  setCookie(COOKIE_REF, landing.handle);
  setCookie(COOKIE_DISCOUNT, landing.discount_code);
  setCookie(COOKIE_LANDING, location.pathname);
  setCookie(COOKIE_NAME, landing.display_name);
}

const ref = getCookie(COOKIE_REF);
const discountCode = getCookie(COOKIE_DISCOUNT);

/**
 * Publishes an analytics event to Shopify customer events (custom pixels can
 * subscribe to `mm_<name>`) and to GA4 when gtag is available on the storefront.
 * @param {string} name
 * @param {Record<string, unknown>} [data]
 */
function mmTrack(name, data) {
  const payload = { influencer: ref || '(none)', ...data };
  const eventName = 'mm_' + name;
  try {
    // @ts-ignore
    window.Shopify?.analytics?.publish?.(eventName, payload);
  } catch (_) {}
  sendToGtag(eventName, payload);
}

/** @type {Array<[string, Record<string, unknown>]>} */
const gtagQueue = [];
let gtagPolls = 0;

/**
 * Shopify's Google pixel defines window.gtag late (after this module runs), so events
 * fired on page load are queued and flushed once gtag is available.
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 */
function sendToGtag(eventName, payload) {
  // @ts-ignore
  if (typeof window.gtag === 'function') {
    try {
      // @ts-ignore
      window.gtag('event', eventName, payload);
    } catch (_) {}
    return;
  }
  gtagQueue.push([eventName, payload]);
  if (gtagQueue.length === 1) setTimeout(flushGtagQueue, 500);
}

function flushGtagQueue() {
  // @ts-ignore
  if (typeof window.gtag !== 'function') {
    gtagPolls += 1;
    if (gtagPolls < 40) setTimeout(flushGtagQueue, 500);
    return;
  }
  for (const [eventName, payload] of gtagQueue.splice(0)) {
    try {
      // @ts-ignore
      window.gtag('event', eventName, payload);
    } catch (_) {}
  }
}

// @ts-ignore
window.mmTrack = mmTrack;

/**
 * Landing page: every link to a product goes through /discount/<code> (so Shopify
 * applies the code server-side) and lands on the product with ?ref=<handle>.
 * @param {{ handle: string, discount_code: string }} config
 */
function decorateLandingLinks(config) {
  /** @type {Array<{ label: string, path: string }>} */
  const products = [];

  for (const link of document.querySelectorAll('a[href]')) {
    if (!(link instanceof HTMLAnchorElement)) continue;
    let url;
    try {
      url = new URL(link.href, location.origin);
    } catch (_) {
      continue;
    }
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) continue;

    const label = (link.textContent || '').trim().slice(0, 40);
    // Only the landing's own buttons count; header and footer menus also link to products.
    if (label && link.closest('main') && !products.some((product) => product.path === url.pathname)) {
      products.push({ label, path: url.pathname });
    }

    url.searchParams.set('ref', config.handle);
    const destination = url.pathname + url.search;
    link.href = config.discount_code
      ? `/discount/${encodeURIComponent(config.discount_code)}?redirect=${encodeURIComponent(destination)}`
      : destination;

    link.addEventListener('click', () => {
      mmTrack('landing_cta_click', { destination: url.pathname, label: (link.textContent || '').trim().slice(0, 60) });
    });
  }

  // Remembered so the product pages can offer a button to the landing's other product.
  setCookie(COOKIE_LINKS, JSON.stringify(products.slice(0, 4)));
}

/**
 * @param {Record<string, unknown>} body
 */
async function updateCart(body) {
  // @ts-ignore
  const base = window.Theme?.routes?.cart_update_url || '/cart/update';
  const response = await fetch(base + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('cart update failed: ' + response.status);
  return response.json();
}

/**
 * @param {any} cart
 * @returns {string[]}
 */
function cartDiscountCodes(cart) {
  return (cart?.discount_codes || []).map((/** @type {{ code: string }} */ discount) => discount.code);
}

/**
 * @param {any} cart
 */
function hasDiscount(cart) {
  return cartDiscountCodes(cart).some((code) => code.toLowerCase() === discountCode.toLowerCase());
}

let syncing = false;
let discountAttempts = 0;

/**
 * Makes sure the cart carries the influencer attributes and the discount code.
 * @param {{ refreshCartUi?: boolean }} [options]
 */
async function syncCart(options = {}) {
  if (!ref || syncing) return;
  syncing = true;
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    /** @type {Record<string, unknown>} */
    const body = {};

    const landingPath = getCookie(COOKIE_LANDING);
    const landingChanged = landingPath && cart?.attributes?.influencer_landing !== landingPath;
    if (cart?.attributes?.influencer !== ref || landingChanged) {
      body.attributes = {
        influencer: ref,
        influencer_landing: landingPath,
      };
    }

    // Bounded so a code the visitor removed by hand (or an invalid one) is not re-added forever.
    const needsDiscount = discountCode && !hasDiscount(cart) && discountAttempts < 2;
    if (needsDiscount) {
      discountAttempts += 1;
      body.discount = [...cartDiscountCodes(cart), discountCode].join(',');
    }

    if (!Object.keys(body).length) return;
    const updated = await updateCart(body);

    if (needsDiscount && options.refreshCartUi && updated?.item_count > 0) {
      document.dispatchEvent(new DiscountUpdateEvent(updated, 'mm-influencer'));
    }
  } catch (error) {
    console.warn('[memelolo] influencer cart sync failed:', error);
  } finally {
    syncing = false;
  }
}

/**
 * Focus mode header: shows "x <INFLUENCER>" next to the store logo.
 * @param {string} name
 */
function renderCobrand(name) {
  const logo = document.querySelector('#header-group .header-logo');
  if (!logo || document.querySelector('.mm-cobrand')) return;

  const cobrand = document.createElement('span');
  cobrand.className = 'mm-cobrand';
  cobrand.innerHTML =
    '<span class="mm-cobrand__times" aria-hidden="true">x</span>' +
    '<span class="mm-cobrand__name">' +
    '<svg class="mm-cobrand__crown" viewBox="0 0 40 22" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" d="M4 19 3 5l9 7 8-10 8 10 9-7-1 14z"/></svg>' +
    '<span class="mm-cobrand__text"></span></span>';
  const text = cobrand.querySelector('.mm-cobrand__text');
  if (text) text.textContent = name;
  // Inside the logo link so both move as one unit in the desktop and mobile header grids.
  logo.appendChild(cobrand);
}

/**
 * @param {string} path
 * @returns {string[]} Significant words of a product handle, e.g. ['pijama', 'adulto'].
 */
function handleWords(path) {
  const handle = path.split('/products/')[1] || '';
  return handle.split(/[-/?#]/).filter((word) => word.length >= 4);
}

/**
 * Focus mode header on product pages: a button to the landing's other product
 * (e.g. "Quiero mi pijama" while viewing the Memelolo). Products of the same family
 * as the current one (handles sharing a word) are skipped.
 */
function renderProductSwitch() {
  if (!location.pathname.includes('/products/') || document.querySelector('.mm-switch')) return;
  const logo = document.querySelector('#header-group .header-logo');
  if (!logo) return;

  /** @type {Array<{ label: string, path: string }>} */
  let products = [];
  try {
    products = JSON.parse(getCookie(COOKIE_LINKS) || '[]');
  } catch (_) {
    return;
  }

  const currentWords = handleWords(location.pathname);
  const target = products.find(
    (product) =>
      product &&
      typeof product.path === 'string' &&
      product.path.includes('/products/') &&
      !handleWords(product.path).some((word) => currentWords.includes(word))
  );
  if (!target) return;

  const button = document.createElement('a');
  button.className = 'mm-switch';
  button.href = target.path + '?ref=' + encodeURIComponent(ref);
  button.textContent = target.label;
  button.addEventListener('click', () => {
    mmTrack('product_switch_click', { destination: target.path, from: location.pathname });
  });
  logo.insertAdjacentElement('afterend', button);
}

if (ref && !landing) {
  renderCobrand(getCookie(COOKIE_NAME) || ref);
  renderProductSwitch();
}

if (landing) {
  decorateLandingLinks(landing);
  mmTrack('landing_view', { landing: location.pathname, utm_campaign: landing.utm_campaign });
}

if (ref) {
  syncCart();
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    syncCart({ refreshCartUi: true });
  });
}
